import axios from 'axios'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

// 개발환경에서만 baseURL 설정 (프로덕션은 같은 서버에서 서빙되므로 불필요)
export const baseURL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000'

if (baseURL) {
  axios.defaults.baseURL = baseURL
}

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8' // 주고 받는 것은 json형태로 함.
axios.defaults.withCredentials = true

const SESSION_EXPIRED_MESSAGE = '로그인 세션이 만료되었습니다. 다시 로그인해주세요.'

const isAuthCheckRequest = (config = {}) => {
  const url = config.url || ''
  return url === '/login' || url === '/api/session'
}

const clearClientLoginState = () => {
  document.cookie = 'userInfo=; Max-Age=0; path=/'
  window.localStorage.removeItem('vuex')
}

const redirectToLoginForExpiredSession = () => {
  if (window.__kaicmsSessionExpiredNotified) return
  window.__kaicmsSessionExpiredNotified = true
  clearClientLoginState()
  alert(SESSION_EXPIRED_MESSAGE)
  if (window.location.pathname !== '/login') {
    window.location.assign('/login?expired=1')
  }
}

// ISO 8601 날짜 형식을 yyyy-MM-dd로 변환하는 함수
const formatISODate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return dateString
  // ISO 8601 형식 체크 (예: 2026-01-12T15:00:00.000Z)
  if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
    const date = new Date(dateString)
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
  return dateString
}

// 객체 내 모든 날짜 필드를 재귀적으로 변환
const transformDates = (obj) => {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) {
    return obj.map(item => transformDates(item))
  }
  if (typeof obj === 'object') {
    const transformed = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        transformed[key] = transformDates(obj[key])
      }
    }
    return transformed
  }
  if (typeof obj === 'string') {
    return formatISODate(obj)
  }
  return obj
}

// axios 응답 인터셉터: 모든 API 응답에서 ISO 날짜를 자동 변환
// 단, 바이너리 응답(Blob, ArrayBuffer)은 그대로 통과시킴 — 그렇지 않으면 빈 객체로 변환되어 다운로드 파일이 깨짐
axios.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      !(typeof Blob !== 'undefined' && response.data instanceof Blob) &&
      !(typeof ArrayBuffer !== 'undefined' && response.data instanceof ArrayBuffer)
    ) {
      response.data = transformDates(response.data)
    }
    return response
  },
  (error) => {
    if (error.response && error.response.status === 401 && !isAuthCheckRequest(error.config)) {
      redirectToLoginForExpiredSession()
    }
    return Promise.reject(error)
  }
)

export default {
  created() {},
  mounted() {},
  unmounted() {},
  methods: {
    async $get(url) {
      try {
        const response = await axios.get(url)
        return response.data
      } catch (e) {
        console.error('API GET 오류:', url, e)
        if (e.response && e.response.data) {
          return {
            success: false,
            status: e.response.status,
            ...e.response.data
          }
        }
        return { success: false, message: '서버와 통신할 수 없습니다.' }
      }
    },
    async $post(url, data) {
      try {
        return await axios.post(url, data)
      } catch (e) {
        console.error('API POST 오류:', url, e)
        if (e.response && e.response.data) {
          return { status: e.response.status, data: e.response.data }
        }
        return { data: { success: false, message: '서버와 통신할 수 없습니다.' } }
      }
    },
    async $put(url, data) {
      try {
        return await axios.put(url, data)
      } catch (e) {
        console.error('API PUT 오류:', url, e)
        if (e.response && e.response.data) {
          return { status: e.response.status, data: e.response.data }
        }
        return { data: { success: false, message: '서버와 통신할 수 없습니다.' } }
      }
    },
    async $delete(url) {
      try {
        return await axios.delete(url)
      } catch (e) {
        console.error('API DELETE 오류:', url, e)
        if (e.response && e.response.data) {
          return { status: e.response.status, data: e.response.data }
        }
        return { data: { success: false, message: '서버와 통신할 수 없습니다.' } }
      }
    },
    // upload ------------------------------------------------------------------
    async $upload(url, file) {
      const formData = new FormData() // formdata는 multipart/form-data로 보내는 것임. 자바스크립트 기본객체임
      formData.append('attachment', file) // name속성이 attachment인 form태그와 같은 의미.
      return (
        await axios
          .post(url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data; accept-charset=utf-8'
            }
          })
          .catch((e) => {
            console.log(e)
          })
      ).data
    },
    // 엑셀다운로드 ---------------------------------------------------------------
    async $ExcelFromTable(
      header = [],
      rows = [],
      fileName = 'excel',
      option = {}
    ) {
      header = header.filter((h) => h.title && h.key)
      // https://github.com/exceljs/exceljs#tables
      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet() // name,{pageSetup:https://github.com/exceljs/exceljs#page-setup}
      ws.addTable({
        name: 'myTable',
        ref: 'A1',
        headerRow: true,
        // style: { theme: 'TableStyleDark3', showRowStripes: true },
        columns: header.map((h) => ({
          name: h.title
        })), // width 설정가능, total함수 가능
        rows: rows.map((r) => header.map((h) => r[h.key])),
        ...option
      })

      saveAs(new Blob([await wb.xlsx.writeBuffer()]), `${fileName}.xlsx`)
    },

    // 날짜 형식 변환 (yyyy-mm-dd)
    $formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 객체의 날짜 필드들을 yyyy-mm-dd 형식으로 변환
    $formatDateFields(obj, dateFields = []) {
      if (!obj) return obj
      dateFields.forEach(field => {
        if (obj[field]) {
          obj[field] = this.$formatDate(obj[field])
        }
      })
      return obj
    },

    // 숫자폼
    $convertNumberFormat(amount, format) {
      let currencySymbol = ''
      let lastSymbol = ''

      if (format.substr(0, 1) !== '#') {
        currencySymbol = format.substr(0, 1)
      }

      if (format.slice(-1) !== '#' && format.slice(-1) !== '0') {
        lastSymbol = format.slice(-1) // #,###%
        format = format.slice(0, -1) // #,###
      }

      let groupingSeparator = '' // 숫자 3자리마다 구분자 , 혹은 .
      let decimalSeparator = '' // 소수점 구분자 , 혹은 .
      let maxFractionDigits = 0 // 소수점 몇자리까지 표기할건지

      if (format.indexOf('.') === -1) {
        // #,###
        groupingSeparator = ','
      } else if (format.indexOf(',') === -1) {
        // #.###
        groupingSeparator = '.'
      } else if (format.indexOf(',') < format.indexOf('.')) {
        // #,###.##
        groupingSeparator = ','
        decimalSeparator = '.'
        maxFractionDigits = format.length - format.indexOf('.') - 1
      } else {
        // #.###,##
        groupingSeparator = '.'
        decimalSeparator = ','
        maxFractionDigits = format.length - format.indexOf(',') - 1
      }

      let sign = '' // amount가 음수로 들어왔을 때
      let dec = 1
      for (let i = 0; i < maxFractionDigits; i++) {
        // dec = 10, i=1
        dec = dec * 10
        // dec = 100
      }
      // dec = 1000

      // amount = -3500 * 100 = 350000 / 100 = -3500.00
      // format = #,###.#0
      let v = String(Math.round(parseFloat(amount) * dec) / dec) // '-3500.00'

      if (v.startsWith('-')) {
        sign = '-'
        v = v.substring(1) // '3500.00'
      }

      if (maxFractionDigits > 0 && format.slice(-1) === '0') {
        v = parseFloat(v).toFixed(maxFractionDigits)
      }

      let d = '' // 소수점 이하 값만
      if (maxFractionDigits > 0 && v.indexOf('.') > -1) {
        d = v.substring(v.indexOf('.')) // .00
        d = d.replace('.', decimalSeparator) // .00 -> ,00
        v = v.substring(0, v.indexOf('.')) // 3500
      }

      const regexp = /(\d+)(\d{3})/

      // v = 3524500

      while (regexp.test(v)) {
        // $1 = 3524, $2 = 500
        // v = 3524,500

        // $1 = 3, $2 = 524
        // v = 3,524,500
        v = v.replace(regexp, '$1' + groupingSeparator + '$2')
      }

      return sign + currencySymbol + String(v) + String(d) + lastSymbol
    }
  }
}
